/**
 * Uygulama kabuğu.
 *
 * SORUMLULUK DAĞILIMI:
 *   - Veri nereden geliyor?  → `store.ts` (Repository)
 *   - Kim giriş yapıyor?      → `AuthGate.tsx`
 *   - Ne gösteriliyor?        → burası (yalnızca düzen + bağlama)
 *
 * HESAPLAMA YOK: AC, can, DC `aggregateCharacter()` ile saf kural motorunda
 * hesaplanır. Bu dosya yalnızca sonucu ekrana basar ve tıklamaları depoları
 * geri gönderir.
 */

import { CONTENT } from '@tttrpg/content'
import type { AuthState, CharacterRow, Repository } from '@tttrpg/db/client'
import {
  aggregateCharacter,
  type CharacterDraft,
  type ComputedSheet,
  draftToCharacterInput,
  type ResourceDef,
  xpForLevel,
} from '@tttrpg/rules'
import {
  CharacterBuilder,
  CharacterSheet,
  DiceOverlay,
  RollHistory,
  type SheetSpells,
  TableView,
} from '@tttrpg/ui'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AccountPanel } from './AccountPanel'
import { AuthGate, InvitePanel, Splash } from './AuthGate'
import { usePublishRolls } from './publishBridge'
import { deriveIdentity, seedCharacterRow, useCharacters, useStore } from './store'

/**
 * API adresi.
 *
 * `VITE_ZARHANE_API` tanımlı değilse boş string döner; o durumda depo
 * factory'si yerel moda düşer (bkz. `store.ts`) ve masa ekranı hiç açılmaz.
 * Buradaki `?? ''` yalnızca tipin `string | undefined` olması nedeniyle —
 * davranış `store.ts`'in sorumluluğunda.
 */
const API_BASE = import.meta.env.VITE_ZARHANE_API ?? ''

/**
 * Masa adı — `VITE_APP_NAME` ortam değişkeninden.
 *
 * Tanımlı değilse "Zarhane"ye düşer (geliştirme varsayılanı). Adı birden
 * çok yerde kullanıyoruz; tek kaynak olması, birini unutmayı önler.
 */
const APP_NAME = import.meta.env.VITE_APP_NAME || 'Zarhane'

export function App() {
  const store = useStore()
  const [auth, setAuth] = useState<AuthState>({
    status: 'unknown',
    userId: null,
    email: null,
    displayName: null,
    role: null,
    viaInvite: false,
    backend: 'local',
  })

  const reloadAuth = useCallback(async () => {
    setAuth(await store.repository.getAuth())
  }, [store.repository])

  useEffect(() => {
    if (store.ready) void reloadAuth()
  }, [store.ready, reloadAuth])

  if (!store.ready) return <Splash text={APP_NAME} />

  return (
    <AuthGate repository={store.repository} auth={auth} onAuthChange={() => void reloadAuth()}>
      <Workbench
        repository={store.repository}
        auth={auth}
        notice={store.notice}
        reloadAuth={reloadAuth}
      />
    </AuthGate>
  )
}

// ---------------------------------------------------------------------------
// ÇALIŞMA ALANI
// ---------------------------------------------------------------------------

function Workbench({
  repository,
  auth,
  notice,
  reloadAuth,
}: {
  repository: Repository
  auth: AuthState
  notice: string | null
  reloadAuth: () => void
}) {
  const characters = useCharacters(repository)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [building, setBuilding] = useState(false)
  const [showInvites, setShowInvites] = useState(false)
  // Masa ekranı: harita + jetonlar + canlı zar akışı.
  const [showTable, setShowTable] = useState(false)
  // Hesap paneli: parola değiştirme + GM unvanı.
  const [showAccount, setShowAccount] = useState(false)
  const [shareLink, setShareLink] = useState<string | null>(null)

  // YEREL MODDA ve liste boşken demo karakteri ekle.
  // Sunucu modunda HİÇBİR ŞEY otomatik oluşturulmaz: herkes kendi
  // karakterini kurar.
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current) return
    if (characters.loading) return
    if (characters.rows.length > 0) return
    if (repository.backend !== 'local') return

    // Aynı oturumda iki kez eklenmesin (React StrictMode iki kez çalıştırır).
    seeded.current = true
    const seed = seedCharacterRow()
    void characters.create({
      name: seed.name,
      alignment: seed.alignment,
      level: seed.level,
      data: seed.data,
      session: seed.session,
      shareToken: null,
    })
  }, [characters, repository.backend])

  const active = useMemo(
    () => characters.rows.find((row) => row.id === activeId) ?? characters.rows[0] ?? null,
    [characters.rows, activeId],
  )

  const setHp = useCallback(
    (value: number, max: number) => {
      if (!active) return
      characters.patch(active.id, {
        session: { ...active.session, currentHp: Math.max(0, Math.min(max, value)) },
      })
    },
    [active, characters],
  )

  const createCharacter = useCallback(
    async (draft: CharacterDraft) => {
      const input = draftToCharacterInput(draft, CONTENT)
      const computed = aggregateCharacter(input, CONTENT)
      const id = await characters.create({
        name: draft.name || 'İsimsiz',
        alignment: draft.alignment,
        level: input.level,
        data: input,
        session: {
          currentHp: computed.defense.hitPoints,
          tempHp: 0,
          pools: {},
        },
        shareToken: null,
      })
      setActiveId(id)
      setBuilding(false)
    },
    [characters],
  )

  const deleteCharacter = useCallback(
    (id: string) => {
      void characters.remove(id)
      if (id === activeId) setActiveId(null)
    },
    [characters, activeId],
  )

  const share = useCallback(async () => {
    if (!active) return
    const token = await characters.share(active.id)
    if (!token) return
    const base = import.meta.env.VITE_APP_URL ?? window.location.origin
    setShareLink(`${base}/?paylas=${token}`)
  }, [active, characters])

  // Paylaşım bağlantısından açıldıysa: giriş gerekmeden karakteri göster.
  const sharedToken = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('paylas')
    } catch {
      return null
    }
  }, [])

  const shared = useSharedCharacter(repository, sharedToken)

  // ⚠️ HOOK TEPEDEKİRLERDEN ÖNCE ÇAĞRILMALIDIR.
  // Aşağıda `if (shared) return ...` gibi erken dönüşler var. Bir hook erken
  // dönüşten SONRA çağrılırsa ve o dönüşün koşulu render'lar arasında
  // değişirse React "hook sayısı değişti" hatası verir. Bu, bazen çalışıp
  // bazen çöken bir hatadır — en kötü tür. Bu yüzden köprü burada, tüm
  // erken dönüşlerden önce kurulur.
  usePublishRolls({
    apiBase: API_BASE,
    backend: repository.backend,
    characterId: active?.id ?? null,
  })

  if (shared) {
    const sharedSheet = aggregateCharacter(shared.data, CONTENT)
    return (
      <>
        <TopBar
          rows={[]}
          activeId={null}
          onPick={() => undefined}
          onNew={() => undefined}
          onDelete={() => undefined}
          auth={auth}
          backend={repository.backend}
          notice={null}
          onToggleInvites={() => setShowInvites((value) => !value)}
          onToggleTable={() => setShowTable((value) => !value)}
          showingTable={showTable}
          onToggleAccount={() => setShowAccount((value) => !value)}
          showingAccount={showAccount}
          onSignOut={() => void reloadAuth()}
        />
        <div className="mx-auto max-w-6xl px-4 py-4">
          <p className="mb-4 rounded border border-gold-dim/40 bg-gold/5 px-3 py-2 text-xs text-gold">
            <strong className="font-display tracking-[0.2em] uppercase">Salt-okunur</strong> — Bu
            karakter senin değil. Düzenlemek için giriş yap ve kendi listenize ekle.
          </p>
          <CharacterSheet
            identity={deriveIdentity(shared)}
            sheet={sharedSheet}
            currentHp={shared.session.currentHp ?? sharedSheet.defense.hitPoints}
            tempHp={shared.session.tempHp ?? 0}
            spells={deriveSpells(shared, sharedSheet.resources)}
          />
        </div>
        <DiceOverlay />
        <RollHistory />
      </>
    )
  }

  const topBar = (
    <TopBar
      rows={characters.rows}
      activeId={active?.id ?? null}
      onPick={setActiveId}
      onNew={() => setBuilding(true)}
      onDelete={deleteCharacter}
      auth={auth}
      backend={repository.backend}
      notice={characters.error ?? notice}
      onToggleInvites={() => setShowInvites((value) => !value)}
      onToggleTable={() => setShowTable((value) => !value)}
      onToggleAccount={() => setShowAccount((value) => !value)}
      showingTable={showTable}
      showingAccount={showAccount}
      onSignOut={() => void repository.signOut().then(reloadAuth)}
    />
  )

  if (showTable && repository.backend === 'worker' && auth.status === 'signed-in') {
    return (
      <>
        {topBar}
        <main className="mx-auto max-w-5xl px-4 py-4">
          <TableView apiBase={API_BASE} />
        </main>
        <DiceOverlay />
        <RollHistory />
      </>
    )
  }

  if (showAccount && repository.backend === 'worker' && auth.status === 'signed-in') {
    return (
      <>
        {topBar}
        <main className="mx-auto max-w-3xl px-4 py-6">
          <h2 className="mb-4 font-display text-lg tracking-[0.2em] text-gold uppercase">Hesap</h2>
          <AccountPanel repository={repository} auth={auth} onChanged={reloadAuth} />
        </main>
      </>
    )
  }

  if (showInvites && repository.backend === 'worker' && auth.role === 'owner') {
    return (
      <>
        {topBar}
        <main className="mx-auto max-w-3xl px-4 py-6">
          <InvitePanel repository={repository} />
        </main>
      </>
    )
  }

  if (building) {
    return (
      <>
        {topBar}
        <CharacterBuilder
          content={CONTENT}
          onCancel={() => setBuilding(false)}
          onComplete={(draft) => void createCharacter(draft)}
        />
        <DiceOverlay />
        <RollHistory />
      </>
    )
  }

  if (characters.loading) return <Splash text="Karakterler yükleniyor…" />

  if (!active) {
    return (
      <>
        {topBar}
        <main className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-4 py-20 text-center">
          <h2 className="font-display text-lg tracking-[0.2em] text-gold uppercase">
            Henüz karakter yok
          </h2>
          <p className="text-sm text-ink-muted">Hazır bir karakteri görmek için bir tane kur.</p>
          <button
            type="button"
            onClick={() => setBuilding(true)}
            className="cursor-pointer rounded border border-gold-dim px-4 py-2 font-display text-sm tracking-[0.2em] text-gold uppercase hover:bg-gold/10"
          >
            Karakter kur
          </button>
        </main>
        <DiceOverlay />
        <RollHistory />
      </>
    )
  }

  const sheet = aggregateCharacter(active.data, CONTENT)
  const maxHp = sheet.defense.hitPoints

  return (
    <>
      {topBar}

      <div className="mx-auto max-w-6xl px-4 py-4">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => void share()}
            className="cursor-pointer rounded border border-line-soft px-2 py-1 text-ink-muted hover:border-gold-dim hover:text-gold"
          >
            Paylaşım bağlantısı üret
          </button>
          {shareLink && (
            <>
              <code className="max-w-full truncate rounded bg-surface-2 px-2 py-1 text-gold-bright">
                {shareLink}
              </code>
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(shareLink)}
                className="cursor-pointer text-ink-muted underline decoration-dotted hover:text-gold"
              >
                kopyala
              </button>
            </>
          )}
        </div>

        <CharacterSheet
          identity={deriveIdentity(active)}
          sheet={sheet}
          currentHp={active.session.currentHp ?? maxHp}
          tempHp={active.session.tempHp ?? 0}
          spells={deriveSpells(active, sheet.resources)}
          onDamage={(amount) => setHp((active.session.currentHp ?? maxHp) - amount, maxHp)}
          onHeal={(amount) => setHp((active.session.currentHp ?? maxHp) + amount, maxHp)}
          onSetHp={(value) => setHp(value, maxHp)}
          onSetTempHp={(value) =>
            characters.patch(active.id, { session: { ...active.session, tempHp: value } })
          }
          onSetPool={(poolId, value) =>
            characters.patch(active.id, {
              session: { ...active.session, pools: { ...active.session.pools, [poolId]: value } },
            })
          }
          onToggleChoice={(groupId, optionId) =>
            characters.patch(active.id, {
              data: {
                ...active.data,
                choices: toggleChoice(sheet, active.data.choices, groupId, optionId),
              },
            })
          }
        />
      </div>

      <DiceOverlay />
      <RollHistory />
    </>
  )
}

/**
 * Seçim listesini aç/kapa.
 *
 * GRUP TÜRÜNE GÖRE DAVRANIŞ — motorun `ChoiceGroup.min/max` kuralı birebir
 * yansıtılır:
 *   - `min === 1` → TEK SEÇİM: yeni seçim eskisinin yerine geçer.
 *   - `min > 1`  → ÇOKLU SEÇİM: azami `max` taneye kadar eklenir.
 *
 * Bu ayrım UI'da YAPILMAZSA kullanıcı "3 tane seçtim ama motor 1 tane
 * uyguladı" diye sorar. Kurallar tek yerde (motor) tanımlıdır; arayüz
 * onları yalnızca okur.
 *
 * `pending` (liste bekleniyor) grupları ve kilitli gruplar DÖNÜŞÜZÜZ kalır:
 * oyuncu içerik eksiğini kendi eliyle dolduramaz.
 */
function toggleChoice(
  sheet: ComputedSheet,
  choices: Record<string, string[]> | undefined,
  groupId: string,
  optionId: string,
): Record<string, string[]> {
  const group = sheet.choices.find((entry) => entry.groupId === groupId)
  if (!group || group.pending || !group.unlocked) return choices ?? {}

  const current = choices?.[groupId] ?? []

  // Zaten seçiliyse kaldır.
  if (current.includes(optionId)) {
    return { ...(choices ?? {}), [groupId]: current.filter((id) => id !== optionId) }
  }

  // 1/N → tek seçim: yenisini ekle, eskilerini düşür.
  if (group.required === 1) {
    return { ...(choices ?? {}), [groupId]: [optionId] }
  }

  // N/M → çoklu seçim, azami sınır var.
  if (current.length >= group.max) return choices ?? {}

  return { ...(choices ?? {}), [groupId]: [...current, optionId] }
}

/**
 * Sheet'in büyü bölümü — oturum verisinden okunur, hesaplanmaz.
 *
 * ÖZEL KAYNAKLAR (KP) BURAYA BİRLESTİRİLİR:
 *   `poolDefs` elle kurulmus havuzlardir (buyucu havuzlari). Yollardan gelen
 *   kaynaklar (vampir KP) ise motorun cikisidir. Ikisini birlestirip tek
 *   listede donduruyoruz; boylece sheet'in havuz barini degistirmeden KP de
 *   gorunur. Havuz OLUSTURULMAZ — sadece GOSTERILIR, deger oturum durumundadir.
 */
function deriveSpells(
  row: CharacterRow,
  resources: (ResourceDef & { max: number })[] = [],
): SheetSpells {
  const pools: SheetSpells['pools'] = (row.session.poolDefs ?? []).map((pool) => ({
    ...pool,
    current: row.session.pools?.[pool.id] ?? pool.max,
  }))

  for (const resource of resources) {
    // Ayni id hem poolDefs'te hem yolda varsa havuz ZATEN listede; ikinci kez
    // eklemek iki KP bari gosterirdi.
    if (pools.some((p) => p.id === resource.id)) continue
    pools.push({
      id: resource.id,
      name: resource.name,
      max: resource.max,
      // Anlik deger KAYITLI degilse azami degerle baslar: yol yeni alindiysa
      // "havuz dolu" olmak beklentidir, aksi halde oyuncu neden 0/5 gordugunu
      // anlamaz ve KP'yi elle artirmasi gerekir.
      current: row.session.pools?.[resource.id] ?? resource.max,
      recharge: resource.recharge,
    })
  }

  return {
    cantrips: row.session.cantrips ?? [],
    prepared: row.session.prepared ?? [],
    pools,
  }
}

/**
 * Paylaşım bağlantısından karakter okuma.
 *
 * GİRİŞ GEREKMEZ — davet bağlantısının amacı tam olarak budur. Hata olursa
 * `null` döner ve uygulama normal akışa devam eder.
 */
function useSharedCharacter(repository: Repository, token: string | null): CharacterRow | null {
  const [row, setRow] = useState<CharacterRow | null>(null)

  useEffect(() => {
    if (!token) {
      setRow(null)
      return
    }
    let cancelled = false
    void repository
      .getSharedCharacter(token)
      .then((found) => {
        if (!cancelled) setRow(found)
      })
      .catch(() => {
        if (!cancelled) setRow(null)
      })
    return () => {
      cancelled = true
    }
  }, [repository, token])

  return row
}

// ---------------------------------------------------------------------------
// ÜST ÇUBUK
// ---------------------------------------------------------------------------

function TopBar({
  rows,
  activeId,
  onPick,
  onNew,
  onDelete,
  auth,
  backend,
  notice,
  onToggleInvites,
  onToggleTable,
  onToggleAccount,
  showingTable,
  showingAccount,
  onSignOut,
}: {
  rows: CharacterRow[]
  activeId: string | null
  onPick: (id: string) => void
  onNew: () => void
  onDelete: (id: string) => void
  auth: AuthState
  backend: Repository['backend']
  notice: string | null
  onToggleInvites: () => void
  onToggleTable: () => void
  onToggleAccount: () => void
  showingTable: boolean
  showingAccount: boolean
  onSignOut: () => void
}) {
  const active = rows.find((row) => row.id === activeId)

  return (
    <header className="sticky top-0 z-30 border-b border-line-soft bg-surface/90 backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-2">
        <span className="font-display text-sm tracking-[0.2em] text-gold uppercase">
          {APP_NAME}
        </span>

        <nav className="flex flex-wrap items-center gap-1">
          {rows.map((row) => {
            const isActive = row.id === activeId
            return (
              <span key={row.id} className="group relative inline-flex">
                <button
                  type="button"
                  onClick={() => onPick(row.id)}
                  className={`cursor-pointer rounded border px-2 py-0.5 text-[0.7rem] transition-colors ${
                    isActive
                      ? 'border-gold bg-gold/20 text-gold-bright'
                      : 'border-line-soft bg-surface-2 text-ink-muted hover:border-gold-dim'
                  }`}
                >
                  {row.name}
                  <span className="tnum ml-1 text-[0.6rem] text-ink-faint">sv{row.level}</span>
                </button>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onDelete(row.id)}
                    title="Karakteri sil"
                    className="ml-0.5 cursor-pointer text-[0.6rem] text-ink-faint opacity-0 transition-opacity group-hover:opacity-100 hover:text-blood"
                  >
                    ✕
                  </button>
                )}
              </span>
            )
          })}
        </nav>

        <button
          type="button"
          onClick={onNew}
          className="cursor-pointer rounded border border-gold-dim px-2 py-0.5 text-[0.7rem] text-gold transition-colors hover:bg-gold/10"
        >
          + Yeni Karakter
        </button>

        <div className="ml-auto flex items-center gap-2 text-[0.65rem]">
          {notice && <span className="text-blood-bright">{notice}</span>}

          {/* MASA: sunucu varsa ve giris yapilmissa. Yerel modda masada
              oyuncu yoktur (tek tarayici), gostermek anlamsiz olurdu. */}
          {backend === 'worker' && auth.status === 'signed-in' && (
            <button
              type="button"
              onClick={onToggleTable}
              className={`cursor-pointer rounded border px-2 py-0.5 transition-colors ${
                showingTable
                  ? 'border-gold bg-gold/20 text-gold-bright'
                  : 'border-line-soft text-ink-muted hover:border-gold-dim hover:text-gold'
              }`}
              title="Harita, jetonlar ve oyuncuların canlı zarları"
            >
              Masa
            </button>
          )}

          {backend === 'worker' && auth.role === 'owner' && (
            <button
              type="button"
              onClick={onToggleInvites}
              className="cursor-pointer rounded border border-line-soft px-2 py-0.5 text-ink-muted hover:border-gold-dim hover:text-gold"
            >
              Davetler
            </button>
          )}

          {backend === 'local' ? (
            <span
              className="text-ink-faint"
              title="Sunucu adresi tanımlı değil; veriler yalnızca bu tarayıcıda"
            >
              yerel mod
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={onToggleAccount}
                className={`cursor-pointer text-ink-faint hover:text-gold ${
                  showingAccount ? 'text-gold' : ''
                }`}
                title="Parola ve görünen ad"
              >
                {auth.displayName}
              </button>
              <button
                type="button"
                onClick={onSignOut}
                className="cursor-pointer text-ink-muted hover:text-blood"
              >
                çıkış
              </button>
            </span>
          )}

          {active && (
            <span className="hidden text-ink-faint sm:inline">
              XP eşiği sv{active.level}: {xpForLevel(active.level)}
            </span>
          )}
        </div>
      </div>
    </header>
  )
}
